import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import analyticsTracker from '../utils/analyticsTracker';

function AIAssistant({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const navigate = useNavigate();

  // Email form state
  const [emailPurpose, setEmailPurpose] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailKeyPoints, setEmailKeyPoints] = useState('');
  const [emailTone, setEmailTone] = useState('professional');

  // Management tips form state
  const [mgmtSituation, setMgmtSituation] = useState('');
  const [mgmtContext, setMgmtContext] = useState('');

  // Grievance tips form state
  const [grievanceIssue, setGrievanceIssue] = useState('');
  const [grievanceDetails, setGrievanceDetails] = useState('');

  const handleGenerateEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult('');
    analyticsTracker.trackFeatureUse('ai_assistant', 'generate_email');

    try {
      const response = await axios.post(`${API}/ai/generate-email`, {
        purpose: emailPurpose,
        recipient: emailRecipient,
        key_points: emailKeyPoints,
        tone: emailTone,
      });
      setResult(response.data.content);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const handleGetManagementTips = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult('');

    try {
      const response = await axios.post(`${API}/ai/management-tips`, {
        situation: mgmtSituation,
        context: mgmtContext,
      });
      setResult(response.data.content);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate tips');
    } finally {
      setLoading(false);
    }
  };

  const handleGetGrievanceTips = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult('');

    try {
      const response = await axios.post(`${API}/ai/grievance-tips`, {
        issue: grievanceIssue,
        details: grievanceDetails,
      });
      setResult(response.data.content);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate tips');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    alert('Copied to clipboard!');
  };

  return (
    <div className="dashboard" data-testid="ai-assistant-page">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img 
            src="/stewie_front.png"
            alt="Stewie Logo"
            className="dashboard-logo-image"
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%' }}
          />
          Ask Stewie
        </div>
        <div className="dashboard-user">
          <button
            className="btn-nav"
            onClick={() => navigate('/')}
            data-testid="back-to-dashboard"
          >
            ← Back to Dashboard
          </button>
          <div className="user-info">
            <div className="user-name" data-testid="user-name">
              {user.name}
            </div>
            <div className="user-role">{user.is_admin ? 'Administrator' : 'User'}</div>
          </div>
          <button className="btn-logout" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="ai-assistant-container">
          <h1 className="ai-assistant-title">
            <span>🤖</span>
            AI Workplace Assistant
          </h1>
          <p className="ai-assistant-subtitle">
            Get help with professional communication, management responses, and workplace grievances
          </p>

          <div className="tabs-container">
            <button
              className={`tab-button ${activeTab === 'email' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('email');
                setResult('');
              }}
              data-testid="email-tab"
            >
              📧 Generate Email
            </button>
            <button
              className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('management');
                setResult('');
              }}
              data-testid="management-tab"
            >
              💼 Management Response
            </button>
            <button
              className={`tab-button ${activeTab === 'grievance' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('grievance');
                setResult('');
              }}
              data-testid="grievance-tab"
            >
              📋 Filing Grievances
            </button>
          </div>

          <div className="ai-form-container">
            {activeTab === 'email' && (
              <form onSubmit={handleGenerateEmail} data-testid="email-form">
                <div className="form-group">
                  <label className="form-label">Purpose of Email</label>
                  <input
                    type="text"
                    className="form-input"
                    value={emailPurpose}
                    onChange={(e) => setEmailPurpose(e.target.value)}
                    placeholder="e.g., Request time off, Follow up on meeting"
                    required
                    data-testid="email-purpose-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Recipient</label>
                  <input
                    type="text"
                    className="form-input"
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value)}
                    placeholder="e.g., Manager, HR Department"
                    required
                    data-testid="email-recipient-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Key Points to Include</label>
                  <textarea
                    className="form-textarea"
                    value={emailKeyPoints}
                    onChange={(e) => setEmailKeyPoints(e.target.value)}
                    placeholder="List the main points you want to communicate..."
                    rows="4"
                    required
                    data-testid="email-keypoints-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tone</label>
                  <select
                    className="form-select"
                    value={emailTone}
                    onChange={(e) => setEmailTone(e.target.value)}
                    data-testid="email-tone-select"
                  >
                    <option value="professional">Professional</option>
                    <option value="formal">Formal</option>
                    <option value="friendly">Friendly</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="btn-generate"
                  disabled={loading}
                  data-testid="generate-email-button"
                >
                  {loading ? 'Generating...' : '✨ Generate Email'}
                </button>
              </form>
            )}

            {activeTab === 'management' && (
              <form onSubmit={handleGetManagementTips} data-testid="management-form">
                <div className="form-group">
                  <label className="form-label">Situation</label>
                  <input
                    type="text"
                    className="form-input"
                    value={mgmtSituation}
                    onChange={(e) => setMgmtSituation(e.target.value)}
                    placeholder="e.g., Received critical feedback, Need to decline request"
                    required
                    data-testid="mgmt-situation-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Context & Details</label>
                  <textarea
                    className="form-textarea"
                    value={mgmtContext}
                    onChange={(e) => setMgmtContext(e.target.value)}
                    placeholder="Provide more context about the situation..."
                    rows="6"
                    required
                    data-testid="mgmt-context-input"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-generate"
                  disabled={loading}
                  data-testid="generate-mgmt-tips-button"
                >
                  {loading ? 'Generating...' : '💡 Get Tips & Language'}
                </button>
              </form>
            )}

            {activeTab === 'grievance' && (
              <form onSubmit={handleGetGrievanceTips} data-testid="grievance-form">
                <div className="form-group">
                  <label className="form-label">Issue</label>
                  <input
                    type="text"
                    className="form-input"
                    value={grievanceIssue}
                    onChange={(e) => setGrievanceIssue(e.target.value)}
                    placeholder="e.g., Workplace harassment, Unfair treatment"
                    required
                    data-testid="grievance-issue-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Details</label>
                  <textarea
                    className="form-textarea"
                    value={grievanceDetails}
                    onChange={(e) => setGrievanceDetails(e.target.value)}
                    placeholder="Describe the issue in detail..."
                    rows="6"
                    required
                    data-testid="grievance-details-input"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-generate"
                  disabled={loading}
                  data-testid="generate-grievance-tips-button"
                >
                  {loading ? 'Generating...' : '📝 Get Grievance Guidance'}
                </button>
              </form>
            )}
          </div>

          {result && (
            <div className="ai-result-container" data-testid="ai-result">
              <div className="result-header">
                <h3>Generated Content</h3>
                <button
                  className="btn-copy"
                  onClick={copyToClipboard}
                  data-testid="copy-button"
                >
                  📋 Copy
                </button>
              </div>
              <div className="result-content" data-testid="result-content">
                {result.split('\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIAssistant;
